<template>
  <md-card>
    <md-card-header class="md-card-header-icon md-card-header-primary">
      <div class="card-icon">
        <md-icon>view_module</md-icon>
      </div>
      <md-toolbar class="fm-card-header">
        <h4 class="md-title">Channel Overrides</h4>
        <help-link :link="helpLink.link" title="channel-list"></help-link>
        <md-button
          class="md-simple"
          @click.native="handleNew()"
          data-tour="channel_new"
        >
          <md-icon>add_circle</md-icon>
          New
        </md-button>
      </md-toolbar>
    </md-card-header>
    <md-card-content>
      <loading-spinner
        v-if="loadingChannels"
        title="Channels"
        v-bind:loadingError="loadingError"
      ></loading-spinner>
      <md-table
        id="channel-listing"
        v-model="channels"
        v-if="!loadingChannels"
        class="table-striped table-hover"
      >
        <md-table-empty-state
          md-label="No Channels Found"
          md-description="It looks like you have no Channels. Go ahead and create one."
        >
          <md-button
            class="md-primary md-raised"
            @click="handleNew()"
            data-tour="channel_create"
          >
            <md-icon>add_circle</md-icon>
            Create
          </md-button>
        </md-table-empty-state>
        <md-table-row
          class="channel handle"
          :class="item.channelIdPattern === '*' ? 'displayNone' : ''"
          slot="md-table-row"
          slot-scope="{ item }"
          :data-channel-id="item.id"
          md-selectable="single"
          @click.stop="handleEdit(item)"
          :data-tour="firstInList(item.id) ? 'channel_edit' : false"
        >
          <md-table-cell md-label="Channel ID Pattern">
            <md-icon>{{
              item.channelIdPattern === "*" ? "" : "reorder"
            }}</md-icon>
            {{
              item.channelIdPattern === "*"
                ? "Default Channel Configuration (*)"
                : item.channelIdPattern
            }}
          </md-table-cell>
          <md-table-cell md-label="Actions" class="actions">
            <md-button
              class="md-just-icon md-danger md-simple"
              :disabled="item.channelIdPattern === '*'"
              @click.native.stop="dataTourActive ? false : handleDelete(item)"
            >
              <md-icon v-if="item.channelIdPattern != '*'">close</md-icon>
            </md-button>
          </md-table-cell>
        </md-table-row>
      </md-table>
    </md-card-content>
  </md-card>
</template>
<script>
import swal from "sweetalert2";
import { get } from "vuex-pathify";
import Sortable from "sortablejs";
import ChannelModel from "@/models/channel";
import _ from "lodash";
import { getCurrentUser } from "../../utils/auth0";
import { isFirstInList } from "../../utils/global";
import { dataTourActive, appcuesIdentify } from "../../utils/appcues";
import { HelpLinkTemplate, LoadingSpinner } from "@/fmComponents";
import helpLinks from "../../contextualHelpLinks.js";

export default {
  props: {
    appId: null
  },
  components: {
    LoadingSpinner,
    "help-link": HelpLinkTemplate,
  },
  computed: {
    channels: get("channels/entities"),
    ogChannels: get("channels/ogEntities"),
    dataTourActive: () => {
      return dataTourActive;
    }
  },
  data: () => ({
    loadingChannels: true,
    loadingError: false,
    helpLink: helpLinks.channelConfig
  }),
  created() {
    this.loadChannels();
  },
  methods: {
    firstInList(id) {
      return isFirstInList(id, "channels");
    },
    getHighestPriorityChannel: function() {
      var me = this;
      const channelRows = document.querySelectorAll(
        "#channel-listing .channel"
      );
      var hightestPriorty = 0;
      channelRows.forEach(row => {
        var channelIndex = me.channels.findIndex(c => {
          return c.id === row.getAttribute("data-channel-id");
        });
        if (hightestPriorty <= me.channels[channelIndex].priority) {
          hightestPriorty = me.channels[channelIndex].priority;
        }
      });
      return hightestPriorty;
    },
    loadChannels: function() {
      var me = this;
      this.$store
        .dispatch("channels/loadEntities", [
          { k: "applicationConfigId", v: this.appId }
        ])
        .then(() => {
          // API returns channels ordered by priority in ascending order,
          // but we need them displayed in descending order, so we reverse.
          me.channels.reverse();
          me.ogChannels.reverse();
          this.loadingChannels = false;
        })
        .catch(ex => {
          this.loadingError = true;
        })
        .finally(() => {
          this.sortableTable();
        });
    },
    updateConfig: function() {
      var me = this;
      var errors = false;

      var dirtyChannels = _.differenceWith(
        this.channels,
        this.ogChannels,
        _.isEqual
      );
      if (dirtyChannels && dirtyChannels.length) {
        var doFinally = function(completed) {
          if (completed === dirtyChannels.length) {
            me.$store.commit(
              "pushSuccess",
              "Your changes have been saved. They will take effect the next time a channel is activated using this channel configuration."
            );

            me.$emit("dirtyChannels", !_.isEqual(me.channels, me.ogChannels));
            if (errors) {
              // If errors have occurred during update, then we cannot be sure of the state.
              // Reloading the channel listing is the easiest way to deal with this.
              // In the future we could track successful vs failed requests and rollback appropriatley.
              me.loadChannels();
            }
          }
        };

        var completed = 0;
        dirtyChannels.forEach(c => {
          this.clean(c);
          this.$store
            .dispatch("channels/updateEntity", c)
            .then(() => {
              doFinally(++completed);
            })
            .catch(() => {
              errors = true;
              doFinally(++completed);
            });
        });
      }
    },
    handleNew() {
      var me = this;
      var newChannel = new ChannelModel();
      swal
        .fire({
          title: `Create Channel`,
          input: "text",
          inputValue: newChannel.channelIdPattern,
          inputPlaceholder: "Channel ID Pattern",
          inputValidator: value => {
            if (!value) {
              return "You must provide a Channel ID Pattern!";
            } else if (value === "*") {
              return "The Channel ID Pattern '*' is reserved.";
            }
          },
          showCancelButton: true,
          confirmButtonClass: "md-button md-success btn-fill",
          cancelButtonClass: "md-button md-danger btn-fill",
          buttonsStyling: false
        })
        .then(result => {
          if (result.value) {
            newChannel.channelIdPattern = result.value;
            newChannel.applicationConfigId = me.appId;
            newChannel.priority = me.getHighestPriorityChannel() + 1;
            this.$store.dispatch("channels/createEntity", newChannel);
            
            var user = getCurrentUser();
            if (user != undefined && !user.lscChannelCreated) {
              user.lscChannelCreated = true;
              appcuesIdentify(user.id, {
                // Unique identifier for current user
                LSC_ChannelCreated: user.lscChannelCreated
              });
              this.$store.dispatch("user/updateEntity", user);
            }
          }
        });
    },
    handleEdit(item) {
      this.$router.push({
        path: `/application/${this.appId}/channel/${item.id}`
      });
    },
    handleDelete(item) {
      swal
        .fire({
          title: "Are you sure?",
          text: `You won't be able to revert this!`,
          type: "warning",
          showCancelButton: true,
          confirmButtonClass: "md-button md-success btn-fill",
          cancelButtonClass: "md-button md-danger btn-fill",
          confirmButtonText: "Yes, delete it!",
          buttonsStyling: false
        })
        .then(result => {
          if (result.value) {
            this.$store.dispatch("channels/deleteEntity", item.id).then(() => {
              swal.fire({
                title: "Deleted!",
                text: `You deleted ${item.channelIdPattern}`,
                type: "success",
                confirmButtonClass: "md-button md-success btn-fill",
                buttonsStyling: false
              });
            });
          }
        });
    },
    sortableTable() {
      var table = document.querySelector("#channel-listing tbody");
      var me = this;

      Sortable.create(table, {
        delay: 500,
        handle: ".handle", // Class selector draggable.
        filter: ".disabled", // Class slector for not draggable.
        onMove: event => {
          // Do not let the non-draggables move (default channel).
          return !event.related.classList.contains("disabled");
        },
        onEnd({ newIndex, oldIndex }) {
          if (newIndex === oldIndex) {
            return; // noop.
          }

          // We just go through and update the priority of all of them according to the new ordering.
          // In some cases this amounts to a noop.
          // For the channel that moved, and the channels that get shifted as a result,
          // this results in a new priority... so channels are dirty in this case.
          const channelRows = document.querySelectorAll(
            "#channel-listing .channel"
          );
          var hightestPriorty = me.getHighestPriorityChannel();

          channelRows.forEach((row, i) => {
            var channelIndex = me.channels.findIndex(c => {
              return c.id === row.getAttribute("data-channel-id");
            });
            if (me.channels[channelIndex].priority !== 0) {
              me.channels[channelIndex].priority =
                me.channels.length - i + hightestPriorty;
            }
          });
          me.$emit("dirtyChannels", !_.isEqual(me.channels, me.ogChannels));
        }
      });
    },
    clean(channel) {
      /* Work around Vue's bug where deleting a value froom a number field sets it to an empty string. */
      if (channel.codec.opus.bitrate === "") {
        channel.codec.opus.bitrate = null;
      }

      if (channel.codec.vp8.bitrate === "") {
        channel.codec.vp8.bitrate = null;
      }
      if (channel.codec.vp9.bitrate === "") {
        channel.codec.vp9.bitrate = null;
      }
      if (channel.codec.h264.bitrate === "") {
        channel.codec.h264.bitrate = null;
      }

      if (channel.sfu.audioInput.maxBitrate === "") {
        channel.sfu.audioInput.maxBitrate = null;
      }
      if (channel.sfu.videoInput.maxBitrate === "") {
        channel.sfu.videoInput.maxBitrate = null;
      }

      if (channel.mcu.audioInput.maxBitrate === "") {
        channel.mcu.audioInput.maxBitrate = null;
      }
      if (channel.mcu.videoInput.maxBitrate === "") {
        channel.mcu.videoInput.maxBitrate = null;
      }
      if (channel.mcu.videoOutput.width === "") {
        channel.mcu.videoOutput.width = null;
      }
      if (channel.mcu.videoOutput.height === "") {
        channel.mcu.videoOutput.height = null;
      }
      if (channel.mcu.videoOutput.frameRate === "") {
        channel.mcu.videoOutput.frameRate = null;
      }

      if (channel.mcu.videoOutput.layoutMargin === "") {
        channel.mcu.videoOutput.layoutMargin = null;
      }

      channel.videoEncodings.forEach((videoEncoding) => {
        if (videoEncoding.bitrate === "") {
          videoEncoding.bitrate = null;
        }
        if (videoEncoding.frameRate === "") {
          videoEncoding.frameRate = null;
        }
        if (videoEncoding.scale === "") {
          videoEncoding.scale = null;
        }
      });
    }
  }
};
</script>
<style lang="scss" scoped>
.displayNone {
  display: none;
}
.channel.handle {
  cursor: move;
  &.disabled {
    cursor: no-drop;
  }
  &.disabled:first-child:last-child {
    cursor: default;
  }
}
</style>
